import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { Shell } from '../../components/Shell'
import { Spinner } from '../../components/ui'

/**
 * What writers have asked Beseter/SPM to do for them.
 *
 * song_publishing_requests has been writable from the writer's catalogue since
 * it was built, and until now nothing read it. A request went in, showed the
 * writer a "requested" pill, and sat there — one had been waiting eighteen
 * days when this screen was written. That is worse than not offering the
 * button, because Plus is partly sold on it.
 *
 * Deliberately its own screen rather than a tab on Support. A support report
 * is finished when it is read; this is a job of work with a real-world
 * counterpart — a registration lodged, a distributor contacted — and its
 * statuses say so.
 */

const KIND = {
  publishing_registration: 'Publishing registration',
  distribution: 'Distribution assistance',
}

const STATUS = {
  requested: 'Waiting on us',
  in_progress: 'Under way',
  completed: 'Done',
  declined: 'Declined',
}

const ago = (iso) => {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso)) / 3.6e6)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const named = (u) =>
  !u ? 'Someone' : [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'Someone'

const FILTERS = [
  { key: 'live', label: 'Outstanding' },
  { key: 'requested', label: 'Waiting on us' },
  { key: 'in_progress', label: 'Under way' },
  { key: 'all', label: 'Everything' },
]

const matches = (r, f) =>
  f === 'all' ? true
  : f === 'live' ? ['requested', 'in_progress'].includes(r.status)
  : r.status === f

export default function Requests() {
  const [rows, setRows] = useState(null)
  const [filter, setFilter] = useState('live')
  const [open, setOpen] = useState(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    document.body.classList.add('modplain')
    return () => document.body.classList.remove('modplain')
  }, [])

  const load = useCallback(async () => {
    setErr(null)
    // "superadmin manages requests" is what makes this visible at all; an
    // ordinary writer's own policy returns only their rows, so this screen is
    // empty rather than wrong if the gate above it ever slips.
    const { data, error } = await supabase.from('song_publishing_requests')
      .select('id, kind, status, note, requested_at, decided_at, decision_note, song:song_id(id, title, song_number), writer:user_id(first_name, last_name, email)')
      .order('requested_at', { ascending: true })
      .limit(500)
    if (error) setErr(readableError(error))
    setRows(data ?? [])
  }, [])

  useEffect(() => { load() }, [load])

  const decide = async (row, status) => {
    setBusy(`${row.id}-${status}`); setErr(null)
    const finished = ['completed', 'declined'].includes(status)
    const { error } = await supabase.from('song_publishing_requests')
      .update({
        status,
        // Only a finished request is decided. Moving one to "under way" is
        // picking it up, not deciding it, and stamping decided_at there would
        // lose the only date that answers "how long did this take".
        decided_at: finished ? new Date().toISOString() : null,
        decision_note: note.trim() || row.decision_note || null,
      })
      .eq('id', row.id)
    setBusy(null)
    if (error) return setErr(readableError(error))
    setNote('')
    load()
  }

  if (rows === null) return <Shell><Spinner /></Shell>

  const shown = rows.filter((r) => matches(r, filter))
  const waiting = rows.filter((r) => r.status === 'requested').length

  // The oldest thing nobody has touched, because the age of the queue is the
  // number that matters here and it is invisible in a list of dates.
  const oldest = rows
    .filter((r) => r.status === 'requested')
    .map((r) => r.requested_at).sort()[0]

  return (
    <Shell>
      <h2 className="m-h1">Publishing requests</h2>
      <p className="m-lede">
        <b className={waiting ? 'bad' : 'good'}>{waiting}</b> waiting on us
        {oldest && <> · oldest <b className="bad">{ago(oldest)}</b></>}
        {' · '}<b>{rows.filter((r) => r.status === 'completed').length}</b> done
      </p>

      <div className="m-filters">
        {FILTERS.map((f) => (
          <button key={f.key} className={`m-filter${filter === f.key ? ' on' : ''}`}
                  onClick={() => setFilter(f.key)}>
            {f.label} ({rows.filter((r) => matches(r, f.key)).length})
          </button>
        ))}
      </div>

      {shown.map((r) => (
        <div key={r.id} className="m-row">
          <div className="m-rowtop">
            <span className={`m-score ${r.status === 'completed' ? 'good' : r.status === 'requested' ? 'bad' : 'grey'}`}>
              {r.status === 'completed' ? '✓' : r.status === 'declined' ? '—' : '!'}
            </span>
            <div className="m-who">
              <b>{r.song?.title || 'Untitled'}</b>
              <small>
                {[named(r.writer), KIND[r.kind] ?? r.kind, STATUS[r.status] ?? r.status]
                  .filter(Boolean).join(' · ')} · asked {ago(r.requested_at)}
              </small>
            </div>
            <button className="m-view" onClick={() => { setOpen(open === r.id ? null : r.id); setNote('') }}>
              {open === r.id ? 'Close' : 'Open'}
            </button>
          </div>

          <div className="m-tags">
            <span className="m-tag">{KIND[r.kind] ?? r.kind}</span>
            {r.status === 'requested' && <span className="m-tag bad">Nobody has picked this up</span>}
            {r.song?.song_number && <span className="m-tag">Song {r.song.song_number}</span>}
          </div>

          {open === r.id && (
            <div className="report">
              <h4 className="m-h4">Who asked</h4>
              <p className="m-small">
                {[named(r.writer), r.writer?.email].filter(Boolean).join(' · ')}
              </p>
              {r.note && <p className="finding-why">{r.note}</p>}
              {r.decision_note && <p className="m-small">Last note: {r.decision_note}</p>}

              <h4 className="m-h4">Where it is up to</h4>
              <input className="m-note" value={note} maxLength={500}
                     placeholder="Note (optional) — kept with the decision"
                     onChange={(e) => setNote(e.target.value)} />
              <div className="m-actions">
                {r.status !== 'in_progress' && (
                  <button className="m-btn" disabled={!!busy} onClick={() => decide(r, 'in_progress')}>
                    {busy === `${r.id}-in_progress` ? 'Saving…' : 'Picking it up'}
                  </button>
                )}
                <button className="m-btn good" disabled={!!busy} onClick={() => decide(r, 'completed')}>
                  {busy === `${r.id}-completed` ? 'Saving…' : 'Done'}
                </button>
                <button className="m-btn bad" disabled={!!busy} onClick={() => decide(r, 'declined')}>
                  {busy === `${r.id}-declined` ? 'Saving…' : 'Decline'}
                </button>
              </div>
              <p className="m-small">
                The writer sees this status on their catalogue, so a note explaining a
                decline is the only thing they will get.
              </p>
              {r.decided_at && <p className="m-small">Decided {ago(r.decided_at)}.</p>}
            </div>
          )}
        </div>
      ))}

      <p className="m-small" style={{ marginTop: 18 }}>
        {rows.length === 0
          ? 'Nothing asked for yet. Plus writers request these from their catalogue.'
          : shown.length === 0 ? 'Nothing in this filter.' : `${shown.length} shown.`}
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
